
// Simple UUID v4 generator
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

import { API_CONFIG, buildApiUrl } from '../config/api';

export interface ErrorLogData {
  error_id?: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: 'auth' | 'api' | 'ui' | 'database' | 'payment' | 'system';
  title: string;
  message: string;
  stack_trace?: string;
  user_id?: number;
  session_id?: string;
  url?: string;
  user_agent?: string;
  ip_address?: string;
  request_data?: unknown;
  response_data?: unknown;
  environment?: 'production' | 'development' | 'staging';
}

export interface ErrorContext {
  user?: { id: number; email?: string };
  request?: {
    url: string;
    method: string;
    headers: Record<string, string>;
    body?: unknown;
  };
  response?: {
    status: number;
    data?: unknown;
  };
}

class ErrorLogger {
  private static instance: ErrorLogger;
  private errorQueue: ErrorLogData[] = [];
  private isSending = false;
  private lastSendTime = 0;
  private errorCounts = new Map<string, { count: number; lastTime: number }>();
  private readonly RATE_LIMIT_WINDOW = 60000; // 1 minute
  private readonly MAX_ERRORS_PER_WINDOW = 10;
  private readonly BATCH_SIZE = 5;
  private readonly BATCH_INTERVAL = 2000; // 2 seconds
  private readonly MAX_QUEUE_SIZE = 100; // Prevent memory leaks
  private readonly CLEANUP_INTERVAL = 300000; // 5 minutes
  private readonly MAX_RETRIES = 3;
  private retryCount = 0;
  private cleanupTimer: NodeJS.Timeout | null = null;

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  constructor() {
    // Start cleanup timer to prevent memory leaks
    this.cleanupTimer = setInterval(() => {
      this.cleanupOldErrors();
    }, this.CLEANUP_INTERVAL);
  }

  async logError(error: Error | string, context?: ErrorContext): Promise<void> {
    try {
      const errorData = this.processError(error, context);
      
      // Rate limiting: prevent too many errors of the same type
      if (this.shouldRateLimit(errorData)) {
        console.warn('[ERROR LOGGER] Rate limited similar error');
        return;
      }
      
      // Prevent queue overflow
      if (this.errorQueue.length >= this.MAX_QUEUE_SIZE) {
        console.warn('[ERROR LOGGER] Error queue full, dropping oldest error');
        this.errorQueue.shift(); // Remove oldest error
      }
      
      // Add to queue for batching
      this.errorQueue.push(errorData);
      
      // Process queue if not already sending
      if (!this.isSending) {
        this.processQueue();
      }
    } catch (logError) {
      // Prevent infinite recursion by not logging errors from the error logger itself
      if (logError instanceof Error && logError.message.includes('[ERROR LOGGER]')) {
        console.error('Error logger recursion detected, stopping:', logError.message);
        return;
      }
      console.error('Failed to log error:', logError);
    }
  }

  private shouldRateLimit(errorData: ErrorLogData): boolean {
    const key = `${errorData.category}_${errorData.title}`;
    const now = Date.now();
    
    const existing = this.errorCounts.get(key);
    
    if (existing) {
      if (now - existing.lastTime < this.RATE_LIMIT_WINDOW) {
        existing.count++;
        existing.lastTime = now;
        
        if (existing.count > this.MAX_ERRORS_PER_WINDOW) {
          return true;
        }
      } else {
        existing.count = 1;
        existing.lastTime = now;
      }
    } else {
      this.errorCounts.set(key, { count: 1, lastTime: now });
    }
    
    return false;
  }

  private cleanupOldErrors(): void {
    const now = Date.now();
    const cutoff = now - (this.RATE_LIMIT_WINDOW * 2); // Keep errors for 2 windows
    
    // Clean up error counts map
    for (const [key, data] of this.errorCounts.entries()) {
      if (data.lastTime < cutoff) {
        this.errorCounts.delete(key);
      }
    }
    
    // Clean up old errors from queue (keep only recent ones)
    const recentErrors = this.errorQueue.filter(error => {
      // Check if error has a timestamp, otherwise assume it's recent
      return !error.created_at || new Date(error.created_at).getTime() > cutoff;
    });
    
    if (recentErrors.length !== this.errorQueue.length) {
      console.log(`[ERROR LOGGER] Cleaned up ${this.errorQueue.length - recentErrors.length} old errors`);
      this.errorQueue = recentErrors;
    }
  }

  private processError(error: Error | string, context?: ErrorContext): ErrorLogData {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const stackTrace = typeof error === 'object' ? error.stack : undefined;

    let category = this.determineCategory(errorMessage, context);
    
    // Validate category is one of the allowed values
    const validCategories = ['auth', 'api', 'ui', 'database', 'payment', 'system'] as const;
    const isValidCategory = validCategories.includes(category);
    
    if (!isValidCategory) {
      console.warn('[ERROR LOGGER] Invalid category detected, defaulting to system:', category);
      category = 'system';
    }
    
    return {
      error_id: generateUUID(),
      severity: this.determineSeverity(errorMessage, context),
      category: category,
      title: this.extractTitle(errorMessage),
      message: errorMessage,
      stack_trace: stackTrace,
      user_id: context?.user?.id,
      session_id: this.getSessionId(),
      url: context?.request?.url || (typeof window !== 'undefined' ? window.location.href : 'server'),
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'server',
      ip_address: undefined, // Will be set by backend
      request_data: this.sanitizeData(context?.request),
      response_data: this.sanitizeData(context?.response),
      environment: 'production' // Default for Cloudflare Workers environment
    };
  }

  private determineSeverity(message: string, context?: ErrorContext): 'critical' | 'high' | 'medium' | 'low' {
    const criticalKeywords = ['system down', 'database connection', 'payment failed', 'security breach', 'out of memory', 'crash'];
    const highKeywords = ['authentication', 'authorization', 'jwt', 'login failed', 'api error', 'network error'];
    const mediumKeywords = ['validation', 'network', 'timeout', 'not found', 'permission denied'];

    const lowerMessage = message.toLowerCase();
    
    if (criticalKeywords.some(keyword => lowerMessage.includes(keyword))) return 'critical';
    if (highKeywords.some(keyword => lowerMessage.includes(keyword))) return 'high';
    if (mediumKeywords.some(keyword => lowerMessage.includes(keyword))) return 'medium';
    if (context?.response?.status && context.response.status >= 500) return 'high';
    if (context?.response?.status && context.response.status >= 400) return 'medium';
    
    return 'low';
  }

  private determineCategory(message: string, context?: ErrorContext): 'auth' | 'api' | 'ui' | 'database' | 'payment' | 'system' {
    const lowerMessage = message.toLowerCase();
    
    if (lowerMessage.includes('auth') || lowerMessage.includes('jwt') || lowerMessage.includes('login')) return 'auth';
    if (lowerMessage.includes('payment') || lowerMessage.includes('momo') || lowerMessage.includes('transaction')) return 'payment';
    if (lowerMessage.includes('database') || lowerMessage.includes('sql') || lowerMessage.includes('query')) return 'database';
    if (context?.request?.url?.includes('/api/')) return 'api';
    if (lowerMessage.includes('render') || lowerMessage.includes('component') || lowerMessage.includes('ui')) return 'ui';
    
    // Default to 'system' for any other category
    return 'system';
  }

  private extractTitle(message: string): string {
    return message.split('\n')[0].substring(0, 100);
  }

  private sanitizeData(data: unknown): string | undefined {
    if (!data) return undefined;

    const sanitized = { ...(data as Record<string, unknown>) };
    const sensitiveKeys = ['password', 'token', 'jwt', 'secret', 'key', 'phone', 'email'];

    const sanitizeObject = (obj: unknown): Record<string, unknown> => {
      if (typeof obj !== 'object' || obj === null) return obj as Record<string, unknown>;

      const result: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
        if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
          result[key] = '[REDACTED]';
        } else if (typeof value === 'object' && value !== null) {
          result[key] = sanitizeObject(value);
        } else {
          result[key] = value;
        }
      }
      return result;
    };

    return JSON.stringify(sanitizeObject(sanitized));
  }

  private getSessionId(): string {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      let sessionId = sessionStorage.getItem('session_id');
      if (!sessionId) {
        sessionId = generateUUID();
        sessionStorage.setItem('session_id', sessionId);
      }
      return sessionId;
    }

    // Server-side: generate a simple session ID
    return generateUUID();
  }

  private async sendToBackend(errorData: ErrorLogData): Promise<void> {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      // Server-side: just log the error, don't try to send to backend
      console.error('[ERROR LOGGER] Server-side error logged:', errorData);
      return;
    }

    // Try both token storage methods for backward compatibility
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    const apiUrl = buildApiUrl(API_CONFIG.ENDPOINTS.ERRORS);

    console.log('[ERROR LOGGER] Attempting to send error to backend:', apiUrl);
    console.log('[ERROR LOGGER] Error data severity:', errorData.severity);
    console.log('[ERROR LOGGER] Error data category:', errorData.category);

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(errorData)
      });

      console.log('[ERROR LOGGER] Backend response status:', response.status);
      const headers = {} as Record<string, string>;
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      console.log('[ERROR LOGGER] Backend response headers:', headers);

      if (!response.ok) {
        console.error('[ERROR LOGGER] Backend request failed:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('[ERROR LOGGER] Error response body:', errorText);
        
        // Don't retry on 4xx errors (client errors)
        if (response.status >= 400 && response.status < 500) {
          console.warn('[ERROR LOGGER] Client error, not retrying');
          return;
        }
      } else {
        console.log('[ERROR LOGGER] Error successfully sent to backend');
      }
    } catch (error) {
      // Prevent infinite recursion by not logging errors from the error logger itself
      if (error instanceof Error && error.message.includes('[ERROR LOGGER]')) {
        console.error('Error logger recursion detected in sendToBackend, stopping:', error.message);
        return;
      }
      console.error('[ERROR LOGGER] Network error sending to backend:', error);
    }
  }

  private async processQueue(): Promise<void> {
    if (this.errorQueue.length === 0) {
      this.isSending = false;
      return;
    }

    this.isSending = true;
    const batch = this.errorQueue.splice(0, this.BATCH_SIZE);
    
    try {
      await this.sendBatchToBackend(batch);
    } catch (error) {
      console.error('[ERROR LOGGER] Failed to send error batch:', error);
    } finally {
      // Schedule next batch
      setTimeout(() => {
        this.processQueue();
      }, this.BATCH_INTERVAL);
    }
  }

  private async sendBatchToBackend(errorBatch: ErrorLogData[]): Promise<void> {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      // Server-side: just log the errors, don't try to send to backend
      console.error('[ERROR LOGGER] Server-side errors logged:', errorBatch.length);
      return;
    }

    // Try both token storage methods for backward compatibility
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    const apiUrl = buildApiUrl(API_CONFIG.ENDPOINTS.ERRORS);

    console.log(`[ERROR LOGGER] Attempting to send ${errorBatch.length} errors to backend:`, apiUrl);

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ errors: errorBatch })
      });

      console.log('[ERROR LOGGER] Backend response status:', response.status);
      const headers = {} as Record<string, string>;
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      console.log('[ERROR LOGGER] Backend response headers:', headers);

      if (!response.ok) {
        if (response.status === 429) {
          if (this.retryCount < this.MAX_RETRIES) {
            this.retryCount++;
            this.errorQueue.unshift(...errorBatch);
            setTimeout(() => this.processQueue(), 5000);
          }
        } else if (response.status >= 500) {
          // Server unavailable — drop batch to prevent infinite retry loops
          this.retryCount = 0;
        } else if (response.status >= 400) {
          // Client error — don't retry
          this.retryCount = 0;
        }
      } else {
        this.retryCount = 0;
      }
    } catch (error) {
      // Prevent infinite recursion by not logging errors from the error logger itself
      if (error instanceof Error && error.message.includes('[ERROR LOGGER]')) {
        console.error('Error logger recursion detected in sendBatchToBackend, stopping:', error.message);
        return;
      }
      console.error('[ERROR LOGGER] Network error sending to backend:', error);
      // Re-add errors to queue for retry
      this.errorQueue.unshift(...errorBatch);
    }
  }
}

export default ErrorLogger.getInstance();
